/* eslint-disable react-hooks/rules-of-hooks */

import { FC, useMemo } from 'react';
import { Heading2, Text } from '@blueprint/ui';
import { Image } from '@blueprint/image';

import { ArticlesForHomepageQuery } from '../../queries/propertyNews.generated';
import {
    PropertyNewsWrapper,
    ArticleSmall,
    ArticleLarge,
    ImageContainer,
    ArticleWrapper,
    StyledHeading,
    MainArticleInfoWrapper,
    MainImageHeading,
    StyledImage,
    MainArticleSnippet,
    MainText,
    HeadingWrapper,
    StyledButton,
    StyledButtonMobile,
    MainArticleWrapperWithFocus,
    LinkFocus,
    StyledSmallArticleImage,
    MainImageContainer
} from './PropertyNews.styles';
import { buildDiscoverLink, formatDate } from '../../../shared/helpers';
import { MAIN_SITE_LINK } from '../../../constants';
import { redirect } from '../../../shared/helpers';
import {
    trackAllPropertyNewsCTA,
    trackIndividualArticle,
    trackMainArticle
} from './PropertyNews.tracking';

const mainHeadlinePropertyNewsText = 'Latest property news';
const propertyNewsText =
    'Everything you need to know about the housing market, explained by our experts.';
const seeAllPropertyNewsCTA = 'See all property news';

const formatArticles = (articles: ArticlesForHomepageQuery['articles']) => {
    const { items = [] } = articles || {};

    return useMemo(() => {
        return items.map((article) => {
            const {
                heroImage,
                title,
                category,
                slug,
                publicationDateAndTime,
                snippet
            } = article || {};

            return {
                imageUrl: heroImage?.url,
                title,
                articleLink: buildDiscoverLink(category?.slug, slug),
                rawDateTime: publicationDateAndTime,
                displayDateTime: formatDate(publicationDateAndTime || ''),
                snippet
            };
        });
    }, [items]);
};

type PropertyNewsProps = {
    articles: ArticlesForHomepageQuery['articles'];
};

export const PropertyNews: FC<PropertyNewsProps> = ({ articles }) => {
    const formattedArticles = formatArticles(articles);

    const firstArticle = formattedArticles[0];
    const remainingArticles = formattedArticles.slice(1);

    if (!firstArticle) {
        return null;
    }

    const discoverLink = `${MAIN_SITE_LINK}/discover/property-news/`;
    const buildDiscoverSectionLink = () => {
        redirect(discoverLink);
    };

    const generateArticleSrcSet = (
        url: string | undefined,
        pixelDensity = 1
    ) => {
        const SIZES = [480, 768, 1024, 1200, 1600, 2400];

        return SIZES.map(
            (width: number) =>
                `${url}?fm=webp&w=${width * pixelDensity}&q=75 ${
                    width * pixelDensity
                }w`
        ).join(', ');
    };

    const generateArticleImageSizes = (): string => `(min-width: 768px) 760px,
    100vw`;

    return (
        <PropertyNewsWrapper>
            <HeadingWrapper>
                <Heading2 size="3">{mainHeadlinePropertyNewsText}</Heading2>
                <StyledButton
                    href={discoverLink}
                    variant="tertiary"
                    onClick={() => {
                        buildDiscoverSectionLink();
                        trackAllPropertyNewsCTA();
                    }}
                    data-testid="see-all-property-news-cta-desktop"
                >
                    {seeAllPropertyNewsCTA}
                </StyledButton>
            </HeadingWrapper>
            <MainText>{propertyNewsText}</MainText>
            <ArticleLarge data-testid="main-property-news-article">
                <MainArticleWrapperWithFocus>
                    <MainImageContainer>
                        <source
                            type="image/webp"
                            media="(-webkit-min-device-pixel-ratio: 2)"
                            srcSet={generateArticleSrcSet(
                                firstArticle?.imageUrl,
                                2
                            )}
                            sizes={generateArticleImageSizes()}
                        />
                        <source
                            type="image/webp"
                            media="(-webkit-min-device-pixel-ratio: 1.5)"
                            srcSet={generateArticleSrcSet(
                                firstArticle?.imageUrl,
                                1.5
                            )}
                            sizes={generateArticleImageSizes()}
                        />
                        <source
                            type="image/webp"
                            media="(-webkit-min-device-pixel-ratio: 1)"
                            srcSet={generateArticleSrcSet(
                                firstArticle?.imageUrl
                            )}
                            sizes={generateArticleImageSizes()}
                        />
                        <StyledImage
                            alt=""
                            src={`${firstArticle?.imageUrl}?w=1200&fm=jpg&fl=progressive`}
                            lazy={true}
                            loading="lazy"
                        />
                    </MainImageContainer>
                    <MainArticleInfoWrapper>
                        <Text size="8">
                            <time
                                dateTime={firstArticle.rawDateTime}
                                data-testid={`ArticlePublishedData-1`}
                            >
                                {firstArticle.displayDateTime}
                            </time>
                        </Text>
                        <MainImageHeading size="5">
                            <LinkFocus
                                plain
                                href={firstArticle.articleLink}
                                data-testid={`article-link-1`}
                                onClick={() => {
                                    trackMainArticle(firstArticle?.title);
                                }}
                            >
                                {firstArticle?.title}
                            </LinkFocus>
                        </MainImageHeading>
                        <MainArticleSnippet>
                            {firstArticle?.snippet}
                        </MainArticleSnippet>
                    </MainArticleInfoWrapper>
                </MainArticleWrapperWithFocus>
            </ArticleLarge>
            <ArticleWrapper>
                {remainingArticles.map((article, index) => {
                    const {
                        imageUrl,
                        title,
                        articleLink,
                        rawDateTime,
                        displayDateTime
                    } = article || {};

                    return (
                        <ArticleSmall key={`${index}-${title}`}>
                            <ImageContainer>
                                <noscript>
                                    <img
                                        src={`${imageUrl}`}
                                        width={640}
                                        loading="lazy"
                                        alt="" // TODO: this needs a true alt
                                    />
                                </noscript>
                                <StyledSmallArticleImage>
                                    <source
                                        type="image/webp"
                                        media="(-webkit-min-device-pixel-ratio: 2)"
                                        srcSet={generateArticleSrcSet(
                                            imageUrl,
                                            2
                                        )}
                                        sizes={generateArticleImageSizes()}
                                    />
                                    <source
                                        type="image/webp"
                                        media="(-webkit-min-device-pixel-ratio: 1.5)"
                                        srcSet={generateArticleSrcSet(
                                            imageUrl,
                                            1.5
                                        )}
                                        sizes={generateArticleImageSizes()}
                                    />
                                    <source
                                        type="image/webp"
                                        media="(-webkit-min-device-pixel-ratio: 1)"
                                        srcSet={generateArticleSrcSet(imageUrl)}
                                        sizes={generateArticleImageSizes()}
                                    />
                                    <Image
                                        alt=""
                                        lazy={true}
                                        loading="lazy"
                                        src={`${imageUrl}?w=640&q=75&fl=progressive`}
                                    />
                                </StyledSmallArticleImage>
                            </ImageContainer>
                            <Text>
                                <time
                                    dateTime={rawDateTime}
                                    data-testid={`ArticlePublishedData-1`}
                                >
                                    {displayDateTime}
                                </time>
                            </Text>
                            <StyledHeading size="7">
                                <LinkFocus
                                    plain
                                    href={articleLink}
                                    data-testid={`article-link-1`}
                                    onClick={() => {
                                        trackIndividualArticle(title);
                                    }}
                                >
                                    {title}
                                </LinkFocus>
                            </StyledHeading>
                        </ArticleSmall>
                    );
                })}
            </ArticleWrapper>
            <StyledButtonMobile
                href={discoverLink}
                variant="tertiary"
                onClick={buildDiscoverSectionLink}
                data-testid="see-all-property-news-cta-mobile"
                isFullWidth
            >
                {seeAllPropertyNewsCTA}
            </StyledButtonMobile>
        </PropertyNewsWrapper>
    );
};
